/**
 * Sentry Utilities
 */

import * as Sentry from '@sentry/nextjs'
import { v4 as uuid } from 'uuid'

import { getUserAuthValues, isUserSignedIn } from '@/utils/general/user'

/**
 * Track airline logo loading failures to Sentry
 *
 * @param airlineCode - The airline code (e.g., 'AI', '6E', 'UK')
 * @param imageUrl - The full URL that failed to load
 * @param componentName - Name of the component where the error occurred
 */
export function trackAirlineLogoError(
  airlineCode: string,
  imageUrl: string,
  componentName: string = 'ImageBlock'
): void {
  // Always log to console in development
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[Sentry] Airline logo failed to load: ${airlineCode}`, { imageUrl, componentName })
  }

  // Send to Sentry
  Sentry.withScope(scope => {
    scope.setLevel('warning')
    scope.setTag('error_type', 'image_load_failure')
    scope.setTag('image_type', 'airline_logo')
    scope.setTag('component', componentName)
    scope.setExtra('image_url', imageUrl)
    scope.setExtra('airline_code', airlineCode)

    // Group similar errors together
    scope.setFingerprint(['airline_logo_load_failure', airlineCode])

    Sentry.captureMessage(`Airline logo failed to load: ${airlineCode}`, 'warning')
  })
}

export function setSentryUser() {
  try {
    let userId = localStorage.getItem('sentry_user_id')
    if (!userId) {
      userId = uuid()
      localStorage.setItem('sentry_user_id', userId)
    }
    Sentry.setUser({ id: userId })
    Sentry.getCurrentScope().setTag('user.is_logged_in', isUserSignedIn())
  } catch (e) {
    console.error('Error setting sentry user', e)
  }
}